import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Stamp } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import { SquigglyUnderline, UpsettingDog } from "@/components/Doodle";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("wc_liked") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    api
      .get("/submissions", { params: { limit: 100 } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const certify = async (id) => {
    if (liked[id]) {
      toast("Already certified. One per weirdo.");
      return;
    }
    try {
      const r = await api.post(`/submissions/${id}/like`);
      setItems((arr) =>
        arr.map((it) => (it.id === id ? { ...it, likes: r.data.likes || it.likes + 1 } : it))
      );
      const nx = { ...liked, [id]: true };
      setLiked(nx);
      localStorage.setItem("wc_liked", JSON.stringify(nx));
      toast.success("Certified damp. ✦");
    } catch {
      toast.error("Something went wet.");
    }
  };

  return (
    <div data-testid="gallery-page">
      <SEO
        title="The Wet Gallery"
        path="/gallery"
        description="Museum of Questionable Talent — community-submitted artwork from beautifully untrained artists. Hit Certify Damp on your favourites."
      />
      <section className="border-b-2 border-[#1A3626] bg-[#EBE7DD] py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="font-display text-xs font-bold uppercase tracking-[0.3em]">
            Community · ongoing exhibition
          </div>
          <h1 className="font-display mt-3 text-5xl font-black leading-[0.9] tracking-tighter sm:text-7xl lg:text-[120px]">
            The Wet Gallery.
          </h1>
          <div className="mt-3 max-w-md">
            <SquigglyUnderline className="h-4 w-full" />
          </div>
          <p className="mt-6 max-w-2xl text-base leading-relaxed md:text-lg">
            Museum of Questionable Talent. Submissions approved by one (1) mildly
            unqualified curator. Hit <em>Certify Damp</em> on your favourites —
            the winners end up on hoodies.
          </p>
          <Link to="/submit" className="wc-btn mt-8" data-testid="gallery-submit-cta">
            Submit Your Weird Little Drawing
          </Link>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {loading ? (
            <div className="columns-2 gap-5 md:columns-3 lg:columns-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="mb-5 h-64 animate-pulse break-inside-avoid border-2 border-[#1A3626] bg-[#EBE7DD]"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center border-2 border-dashed border-[#1A3626] p-16 text-center"
              data-testid="gallery-empty"
            >
              <UpsettingDog className="h-32 w-32 wc-wobble" />
              <h3 className="font-display mt-6 text-2xl font-black uppercase tracking-wide">
                The gallery is dry.
              </h3>
              <p className="mt-2 max-w-md">
                No art has been approved yet. Be the first to embarrass
                yourself in public.
              </p>
              <Link to="/submit" className="wc-btn mt-6">
                Submit something weird
              </Link>
            </div>
          ) : (
            <div
              className="columns-2 gap-5 md:columns-3 lg:columns-4"
              data-testid="gallery-grid"
            >
              {items.map((g, i) => (
                <article
                  key={g.id}
                  className="wc-card mb-5 block break-inside-avoid overflow-hidden wc-fade-up"
                  style={{ animationDelay: `${(i % 8) * 40}ms` }}
                  data-testid={`artwork-card-${g.id}`}
                >
                  <div className="overflow-hidden bg-[#F9F9F4]">
                    <img
                      src={fileUrl(g.image_url)}
                      alt={g.title}
                      className="w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="border-t-2 border-[#1A3626] p-4">
                    <div className="font-display text-base font-bold uppercase tracking-wide">
                      {g.title}
                    </div>
                    <div className="mt-1 text-xs text-[#1A3626]">
                      by <span className="font-bold">{g.artist_name}</span>
                      {g.handle ? ` · @${g.handle.replace(/^@/, "")}` : ""}
                    </div>
                    {g.description && (
                      <p className="mt-2 line-clamp-3 text-sm">{g.description}</p>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <button
                        onClick={() => certify(g.id)}
                        className={`font-display inline-flex items-center gap-2 border-2 border-[#1A3626] px-3 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                          liked[g.id]
                            ? "bg-[#93C065] text-[#0F0F0F]"
                            : "bg-transparent hover:bg-[#1A3626] hover:text-[#F9F9F4]"
                        }`}
                        data-testid={`certify-damp-${g.id}`}
                      >
                        <Stamp className="h-3.5 w-3.5" strokeWidth={3} />
                        Certify Damp
                      </button>
                      <div className="flex items-center gap-1 text-sm font-bold text-[#1A3626]">
                        <Heart
                          className="h-4 w-4"
                          strokeWidth={2.5}
                          fill={liked[g.id] ? "#93C065" : "none"}
                        />
                        <span data-testid={`like-count-${g.id}`}>{g.likes}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
